import type {PublicMethodUtils} from '@wix/document-services-types'

export default function (publicMethodUtils: PublicMethodUtils) {
    const {
        extensionPublicAPI: {immediate, getter, dataManipulation}
    } = publicMethodUtils

    return {
        methods: {
            /** @class documentServices.bindings*/
            bindings: {
                /**
                 * Binds a context to a bound object, the bindingMap will be merged to the existing bindings
                 * It can be used to bind a context to a bound object for the first time
                 * @member documentServices.bindings.bind
                 * @param {BoundObjectRef} boundRef - the bound object to bind the context to
                 * @param {string} contextType - the context to bind to the bound object
                 * @param {Object} bindingsMap - a key value map from component property to a context property
                 */
                bind: immediate('bindings.bind'),
                /**
                 * Binds a context to a bound object, the bindingMap will replace the existing bindings
                 * It can be used to bind a context to a bound object for the first time
                 * @member documentServices.bindings.bindAll
                 * @param {BoundObjectRef} boundRef - the bound object to bind the context to
                 * @param {string} contextType - the context to bind to the bound object
                 * @param {Object} bindingsMap - a key value map from component property to a context property
                 */
                bindAll: immediate('bindings.bindAll'),
                /**
                 * Unbinds specific properties of a specific context from a bound object
                 * It does not remove the bindingMap itself
                 * @member documentServices.bindings.unbindProperties
                 * @param {BoundObjectRef} boundRef - the bound object to unbind properties from
                 * @param {string[]} properties - a list of properties to unbind from the bound object
                 */
                unbindProperties: immediate('bindings.unbindProperties'),
                /**
                 * Returns all the bindings of a bound object
                 * @member documentServices.bindings.getBoundProperties
                 * @param {BoundObjectRef} boundRef - the bound object whose bindings are requested
                 * @returns {BoundPropertiesPerProvider} - a map keyed by context type, function library type, or template component ID to BoundProperties (each entry includes bindingType plus contextType/functionLibraryType/templateComponentId and bindingsMap)
                 */
                getBoundProperties: getter('bindings.getBoundProperties'),
                /**
                 * Unbinds a bound object from all contexts completely (removes the bindings list)
                 * @member documentServices.bindings.unbind
                 * @param {BoundObjectRef} boundRef - the bound object to unbind from all contexts
                 */
                unbind: dataManipulation('bindings.unbind'),
                /** @class documentServices.bindings.contexts */
                contexts: {
                    /**
                     * Lists all bound objects (components, context configurations, inner elements) that have bindings to a specific context type
                     * @member documentServices.bindings.contexts.listBoundObjects
                     * @param {string} contextType - the context type to search for
                     * @param {ListContextBoundObjectsOptions} [options] - optional options to restrict the search scope
                     * @returns {BoundObjectOrInnerElement[]} - list of bound objects and inner elements
                     */
                    listBoundObjects: getter('bindings.contexts.listBoundObjects')
                },
                /** @class documentServices.bindings.functions */
                functions: {
                    /**
                     * Binds component properties to functions from a function library
                     * @member documentServices.bindings.functions.bind
                     * @param {BoundObjectRef} boundRef - the bound object to bind the functions to
                     * @param {string} functionLibraryType - the function library type
                     * @param {FunctionBindingsMap} functionBindingsMap - map of property names to function bindings
                     */
                    bind: immediate('bindings.functions.bind'),
                    /**
                     * Lists all bound objects (components, context configurations, inner elements) that have bindings to a specific function library type
                     * @member documentServices.bindings.functions.listBoundObjects
                     * @param {string} functionLibraryType - the function library type to search for
                     * @returns {BoundObjectOrInnerElement[]} - list of bound objects and inner elements
                     */
                    listBoundObjects: getter('bindings.functions.listBoundObjects')
                },
                /** @class documentServices.bindings.templates */
                templates: {
                    /**
                     * Binds a component inside a repeater template to item fields from a list property.
                     * Uses TemplateItem binding type with itemPath relative to each list item.
                     * @member documentServices.bindings.templates.bind
                     * @param {CompRef | BoundObjectRef} componentRef - the component inside the template to bind
                     * @param {string} templateComponentId - the template component ID
                     * @param {TemplateBindingsMap} bindingsMap - map from component property to TemplateItemBinding or TemplateMethodBinding or TemplateIndexBinding or TemplateLengthBinding
                     */
                    bind: immediate('bindings.templates.bind'),
                    /**
                     * Lists all the template bindings of a bound object
                     * @member documentServices.bindings.templates.list
                     * @param {BoundObjectRef} boundRef - the bound object whose template bindings are requested
                     * @returns {TemplateListItem[]} - a list of template bindings
                     */
                    list: immediate('bindings.templates.list')
                }
            }
        }
    }
}
